import React, { useState, useEffect } from 'react';

import { Box, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CheckCircle, Cancel } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  answerIcon: {
    fontSize: 24,
    margin: '0 9px',
  },
  checkIcon: {
    color: '#4caf50',
    fontSize: 60,
    marginBottom: theme.spacing(2),
  },
  correctAnswer: {
    backgroundColor: '#B4EAB7',
    border: '1px solid #388E3C',
    color: '#388E3C',
    fontWeight: 'bold',
  },
  incorrectAnswer: {
    border: '1px solid red',
    color: 'red',
  },
  questionText: {
    color: '#2AAAE2',
    fontSize: 22,
    fontWeight: 600,
  },
  radioGroup: {
    marginTop: 8,
    width: '100%',
  },
  radioOption: {
    '&.confirmed': {
      backgroundColor: '#e8f5e9',
      borderColor: '#4caf50',
    },
    '&.selected': {
      borderColor: 'none',
    },
    borderRadius: 5,
    height: '30px',
    marginLeft: 8,
    marginTop: 8,
    padding: 8,
  },
  stepContainer: {
    marginTop: 24,
    minHeight: '200px',
  },
  subtitle: {
    color: '#172B4D',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 1.5,
    textAlign: 'center',
  },
  title: {
    color: '#2AAAE2',
    fontSize: 30,
    fontWeight: '500',
    marginBottom: 20,
    textAlign: 'center',
  },
  videoWatchedContainer: {
    alignItems: 'center',
    color: '#2AAAE2',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    textAlign: 'center',
  },
}));

const EvaluationStepContent = ({ activeStep, answerChecked, answers, handleAnswerChange, questions, previewMode, onClose = () => null }) => {
  const classes = useStyles();

  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (activeStep >= questions.length && previewMode) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            onClose(true);

            return 0;
          }

          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }

    return () => null;
  }, [activeStep, questions.length, previewMode, onClose]);

  if (activeStep >= questions.length) {
    if (previewMode) {
      return (
        <Box className={classes.videoWatchedContainer}>
          <Box className={classes.title}>Evaluation Preview Complete</Box>

          <Box className={classes.subtitle}>
            You have reviewed all evaluation questions.
            <br />
            Automatically continuing in {countdown} seconds...
          </Box>
        </Box>
      );
    }

    return (
      <Box className={classes.videoWatchedContainer}>
        <Box className={classes.title}>Video watched</Box>

        <Box className={classes.subtitle}>
          You have completed all evaluation questions.
          <br /> Please confirm to submit your responses.
        </Box>
      </Box>
    );
  }

  const currentQuestion = questions[activeStep];

  return (
    <Box className={classes.stepContainer}>
      <Box className={classes.questionText}>{currentQuestion.text}</Box>

      <RadioGroup value={answers[currentQuestion.id]} onChange={!answerChecked ? handleAnswerChange(currentQuestion.id) : null} className={classes.radioGroup}>
        {currentQuestion.options.map((option) => {
          let optionClass = classes.radioOption;
          const optionId = option.id.toString();

          const isCorrect = optionId === currentQuestion.correctOptionId.toString();
          const isSelected = answers[currentQuestion.id] === optionId;

          if (answerChecked && isCorrect) {
            optionClass += ` ${classes.correctAnswer}`;
          } else if (answerChecked && isSelected && !isCorrect) {
            optionClass += ` ${classes.incorrectAnswer}`;
          } else if (isSelected) {
            optionClass += ' selected';
          }

          if (answerChecked) {
            const isWrongSelection = isSelected && !isCorrect;

            return (
              <Box key={option.id} className={optionClass} display="flex" alignItems="center" p={1}>
                {!isWrongSelection && !isCorrect && <Radio color="primary" checked={isSelected} disabled value={option.id} />}
                {isWrongSelection && <Cancel className={classes.answerIcon} style={{ color: 'red' }} />}
                {isCorrect && <CheckCircle className={classes.answerIcon} style={{ color: '#4caf50' }} />}
                <Box flexGrow={1}>{option.text}</Box>
              </Box>
            );
          }

          return <FormControlLabel key={option.id} value={option.id} control={<Radio color="primary" checked={isSelected} />} label={option.text} className={optionClass} />;
        })}
      </RadioGroup>
    </Box>
  );
};

export default EvaluationStepContent;
